import api from './api';

const withStoreId = (storeId) => (storeId ? { _storeId: storeId } : undefined);

const TeamMemberService = {
  checkTeamMemberEmail: async (email, storeId) => {
    const response = await api.get('/team-members/email-check', {
      params: { email },
      ...withStoreId(storeId)
    });
    return response.data;
  },

  getTeamMembersByStore: async (storeId) => {
    try {
      const response = await api.get('/team-members', withStoreId(storeId));
      return response.data;
    } catch (error) {
      console.error('Failed to load team members:', error);
      throw error;
    }
  },

  // Get services by store ID
  getServicesByStore: async (storeId) => {
    try {
      const response = await api.get('/services', withStoreId(storeId));
      return response.data;
    } catch (error) {
      console.error('Failed to load services:', error);
      throw error;
    }
  },

  // Get single team member by ID
  getTeamMember: async (teamMemberId, storeId) => {
    const response = await api.get(`/team-members/${teamMemberId}`, withStoreId(storeId));
    return response.data;
  },

  // Create new team member
  createTeamMember: async (teamMemberData) => {
    const response = await api.post('/team-members', teamMemberData);
    return response.data;
  },

  // Update team member
  updateTeamMember: async (teamMemberId, teamMemberData) => {
    const response = await api.put(`/team-members/${teamMemberId}`, teamMemberData);
    return response.data;
  },

  // Update team member status (enable/disable)
  updateTeamMemberStatus: async (teamMemberId, disabled) => {
    const response = await api.patch(`/team-members/${teamMemberId}/status`, { disabled });
    return response.data;
  },

  // Set team member profile picture
  setProfilePicture: async (teamMemberId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.patch(`/team-members/${teamMemberId}/profile-picture`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Delete team member profile picture
  deleteProfilePicture: async (teamMemberId) => {
    await api.delete(`/team-members/${teamMemberId}/profile-picture`);
  },

  updateTeamMemberWorkingHours: async (teamMemberId, storeId, workingHours) => {
    const response = await api.put(`/team-members/${teamMemberId}/working-hours`, { workingHours }, withStoreId(storeId));
    return response.data;
  },

  getTeamMemberTimeOffs: async (teamMemberId, storeId, dateRange) => {
    const params = new URLSearchParams();
    params.append('dateRange.from', dateRange.from);
    params.append('dateRange.to', dateRange.to);
    const response = await api.get(`/team-members/${teamMemberId}/time-offs?${params.toString()}`, withStoreId(storeId));
    return response.data;
  },

  createTeamMemberTimeOff: async (teamMemberId, storeId, payload) => {
    const response = await api.post(`/team-members/${teamMemberId}/time-offs`, payload, withStoreId(storeId));
    return response.data;
  },

  deleteTeamMemberTimeOff: async (teamMemberId, timeOffId, storeId) => {
    await api.delete(`/team-members/${teamMemberId}/time-offs/${timeOffId}`, withStoreId(storeId));
  },

  getSpecialOpenings: async (storeId, dateRange) => {
    const params = new URLSearchParams();
    params.append('dateRange.from', dateRange.from);
    params.append('dateRange.to', dateRange.to);
    const response = await api.get(`/stores/${storeId}/special-openings?${params.toString()}`, withStoreId(storeId));
    return response.data;
  },

  upsertSpecialOpenings: async (storeId, payload) => {
    const response = await api.post(`/stores/${storeId}/special-openings`, payload, withStoreId(storeId));
    return response.data;
  },

  deleteSpecialOpening: async (storeId, id) => {
    await api.delete(`/stores/${storeId}/special-openings/${id}`, withStoreId(storeId));
  },

  deleteSpecialOpenings: async (storeId, ids) => {
    const params = new URLSearchParams();
    (ids || []).forEach((id) => params.append('ids', String(id)));
    await api.delete(`/stores/${storeId}/special-openings?${params.toString()}`, withStoreId(storeId));
  },

  // Delete team member
  // NOTE: This endpoint doesn't exist in the backend.
  deleteTeamMember: async (teamMemberId) => {
    throw new Error('Delete team member endpoint not implemented.');
  }
};

export default TeamMemberService;
